import { css } from "@compiled/react";

import {
    SHOW_RECOMMENDED_TEMPLATES_PATH,
    useRecommendedTemplates
} from "@/components/confluence/templates/context/recommended-templates-context";
import { useTemplateFuzzySearchContext } from "@/components/confluence/templates/context/template-fuzzy-search-context";
import { SEOCategoryLink } from "@/components/confluence/templates/seo-category-link";
import { SEOTemplateLink } from "@/components/confluence/templates/seo-template-link";
import { Skeleton } from "@/components/confluence/templates/skeleton";
import { SkeletonTemplateRecommendationCardGrid } from "@/components/confluence/templates/skeleton-template-card-grid.tsx";
import { TemplateCardGrid } from "@/components/confluence/templates/template-card-grid";
import { TemplateCategoriesUnifiedSearch } from "@/components/confluence/templates/template-categories-unified-search";
import { TemplateSectionWrapper } from "@/components/confluence/templates/template-section-wrapper";
import { useFeatureExposed } from "@/utils/feature-exposed";

const skeletonTopPanel = css({
    width: "100%"
});

const displayPanelStyle = css({
    width: "100%",
    overflowX: "auto",
    position: "sticky",
    insetBlockStart: "56px",
    zIndex: 1,
    background: "#fff",
    padding: "16px 0"
});

const hiddenStyle = css({
    display: "none"
});

const CategoryPanel = ({
    isShowRecommendedTemplatesRoute
}: {
    isShowRecommendedTemplatesRoute: boolean;
}) => {
    const {
        enrolledInRecommended,
        recommendedTemplatesStatus,
        adjustedCategories
    } = useRecommendedTemplates();

    if (isShowRecommendedTemplatesRoute && enrolledInRecommended) {
        if (
            recommendedTemplatesStatus === "pending" ||
            recommendedTemplatesStatus === null
        ) {
            return (
                <div css={skeletonTopPanel}>
                    <ul css={hiddenStyle} data-testid="category-links">
                        {adjustedCategories.map((category) => (
                            <li key={"seo-" + category.id}>
                                <SEOCategoryLink category={category} />
                            </li>
                        ))}
                    </ul>
                    <Skeleton
                        width="100%"
                        height="108px"
                        styles={{ borderRadius: "20px" }}
                    />
                </div>
            );
        }
    }

    return <TemplateCategoriesUnifiedSearch />;
};

const DisplayTemplates = ({
    isShowRecommendedTemplatesRoute
}: {
    isShowRecommendedTemplatesRoute: boolean;
}) => {
    const { displayedTemplates } = useTemplateFuzzySearchContext();
    const { enrolledInRecommended, recommendedTemplatesStatus } =
        useRecommendedTemplates();

    if (isShowRecommendedTemplatesRoute && enrolledInRecommended) {
        if (
            recommendedTemplatesStatus === "pending" ||
            recommendedTemplatesStatus === null
        ) {
            return <SkeletonTemplateRecommendationCardGrid />;
        }
    }

    return <TemplateCardGrid templates={displayedTemplates} />;
};

export const UnifiedSearchResults = () => {
    const { displayedTemplates } = useTemplateFuzzySearchContext();
    const term = location.pathname;
    const isShowRecommendedTemplates = term === SHOW_RECOMMENDED_TEMPLATES_PATH;

    useFeatureExposed("confluence-template-recommendations-child-exp", "test", {
        conditions: [isShowRecommendedTemplates]
    });

    return (
        <TemplateSectionWrapper columnOrientation={true}>
            <div css={displayPanelStyle}>
                <CategoryPanel
                    isShowRecommendedTemplatesRoute={isShowRecommendedTemplates}
                />
            </div>
            <DisplayTemplates
                isShowRecommendedTemplatesRoute={isShowRecommendedTemplates}
            />
            <ul css={hiddenStyle} data-testid="template-links">
                {displayedTemplates.map((template) => (
                    <li key={"seo-" + template.templateId}>
                        <SEOTemplateLink template={template} />
                    </li>
                ))}
            </ul>
        </TemplateSectionWrapper>
    );
};
